// cSpell:ignore Afgehandeld afgehandeld Afrekeningen Bedrag Betaalstatus betalen Bevestigd Eindafrekening Gebouw Kost laste Naam Niets Periode Privaat Voorbeeldstraat
// The columns of a settlement row (Lewis, 2026-10-16: "carry the style over
// to Afrekeningen", what each column says, and the cost list's order), in
// this order:
//   Naam | Datum | Type | Periode | Status | Bedrag
// then the row menu, as the cost list reads Kost | Datum | Type | Periode.
// Datum is the day the settlement was confirmed. Type ("Gebouw" or
// "Privaat") is only there inside a building; the /costs overview shows the
// property in its place, and a unit on its own has neither. The amounts are
// in the drawer and on the statement; the list shows only the total of the
// costs.
//
// The settlement list sits on the cost list's grid, so the two tables read
// as one: Naam, Datum, Type (or the property) and Periode start where Kost,
// Datum, Type and Periode start, and Status where the cost's Status starts,
// with the same gaps at every screen width. The period also takes the room
// of "Ten laste", which a settlement has nothing for. Status is how far the
// tenants are with paying (Lewis, 2026-10-21: a settlement is only stored
// once it is confirmed, so "Bevestigd" said nothing and its column went);
// it lines up with the cost's Status, and Bedrag, the total of the
// settlement's costs, with the cost's Bedrag (Lewis, 2026-10-21). Pure, so
// the order,
// the widths and the texts are testable; SettlementsTable only decorates
// them.

import { getLocalizedText } from "@rentiohq/shared-frontend/dist/utils/i18n/i18n.utils";
import { TCostLevel } from "./costLevels.utils";
import {
  COST_LIST_MENU_WIDTH,
  costListColumns,
  costListColumnWidth,
  costTypeAppearance,
  ECostListColumn,
  TCostListAlign,
  TCostTypeAppearance,
} from "./costListColumns.utils";
import { costDayText, periodRangeText } from "./costPeriod.utils";
import { toBrusselsIsoDay } from "./localDay.utils";
import { fromCents, toCents, vatOfAmount } from "./money.utils";
import { settlementKindOf, settlementTitleOf } from "./settlementName.utils";
import { vatPercent } from "./types";
import type { Settlement, SettlementAction } from "./types";

export enum ESettlementListColumn {
  Name = "name",
  // "Datum": the day the settlement was confirmed.
  ConfirmedOn = "confirmed_on",
  Period = "period",
  Level = "level",
  Property = "property",
  // "Status": how far the tenants are with paying.
  PayStatus = "pay_status",
  // "Bedrag": the total of the settlement's costs.
  Amount = "amount",
}

// Which page the list is on: inside a building (a unit of one, or the
// building itself) it has a Type column, across properties (the /costs
// overview) the property.
export interface ISettlementListPage {
  inBuilding: boolean;
  acrossProperties: boolean;
}

// The columns of a settlement list, in order: the cost list's order (see
// costListColumns), Type or the property right after the date.
export const settlementListColumns = (
  page: ISettlementListPage,
): ESettlementListColumn[] => {
  const levelColumns: ESettlementListColumn[] = [];
  if (page.inBuilding) levelColumns.push(ESettlementListColumn.Level);
  else if (page.acrossProperties) {
    levelColumns.push(ESettlementListColumn.Property);
  }
  return [
    ESettlementListColumn.Name,
    ESettlementListColumn.ConfirmedOn,
    ...levelColumns,
    ESettlementListColumn.Period,
    ESettlementListColumn.PayStatus,
    ESettlementListColumn.Amount,
  ];
};

// The cost list's columns each settlement column takes the place of, on the
// same page: each its namesake, the period also "Ten laste", which a
// settlement has nothing for.
const costColumnsOf = (params: {
  column: ESettlementListColumn;
  page: ISettlementListPage;
}): ECostListColumn[] => {
  const { column } = params;
  switch (column) {
    case ESettlementListColumn.Name:
      return [ECostListColumn.Cost];
    case ESettlementListColumn.ConfirmedOn:
      return [ECostListColumn.InvoiceDate];
    case ESettlementListColumn.Period:
      return [ECostListColumn.Period, ECostListColumn.ChargedTo];
    case ESettlementListColumn.Level:
      return [ECostListColumn.Type];
    case ESettlementListColumn.Property:
      return [ECostListColumn.Property];
    case ESettlementListColumn.PayStatus:
      return [ECostListColumn.Status];
    case ESettlementListColumn.Amount:
      return [ECostListColumn.Amount];
  }
};

// How many of the grid's tracks a column spans.
export const settlementListColumnSpan = (params: {
  column: ESettlementListColumn;
  page: ISettlementListPage;
}): number => costColumnsOf(params).length;

// The grid of a settlement row: the cost list's tracks on the same page,
// then the row menu. Fixed and
// table-relative widths only (see costListColumns.utils), so no width
// depends on a row's content, and a building page fits a 1024 px wide screen
// without a sideways scroll.
export const settlementListGridTemplate = (page: ISettlementListPage): string =>
  [
    ...costListColumns(page).map(costListColumnWidth),
    `${COST_LIST_MENU_WIDTH}px`,
  ].join(" ");

const HEADER_KEYS: Record<ESettlementListColumn, string> = {
  [ESettlementListColumn.Name]: "costs.field.name",
  // The cost list's word for its date column.
  [ESettlementListColumn.ConfirmedOn]: "costs.field.date",
  [ESettlementListColumn.Period]: "costs.field.period",
  // The cost list's words for the same columns.
  [ESettlementListColumn.Level]: "costs.list.column.type",
  [ESettlementListColumn.Property]: "costs.field.property",
  // The cost list's words: one status column and one amount in both lists.
  [ESettlementListColumn.PayStatus]: "costs.detail.status",
  [ESettlementListColumn.Amount]: "costs.field.amount",
};

export const settlementListColumnHeader = (
  column: ESettlementListColumn,
): string => getLocalizedText(HEADER_KEYS[column]);

// Placed as the cost list places the column it lines up with: all left
// (Lewis, 2026-10-18: the date and the type too).
const COLUMN_ALIGN: Record<ESettlementListColumn, TCostListAlign> = {
  [ESettlementListColumn.Name]: "left",
  [ESettlementListColumn.ConfirmedOn]: "left",
  [ESettlementListColumn.Period]: "left",
  [ESettlementListColumn.Level]: "left",
  [ESettlementListColumn.Property]: "left",
  [ESettlementListColumn.PayStatus]: "left",
  [ESettlementListColumn.Amount]: "left",
};

export const settlementListColumnAlign = (
  column: ESettlementListColumn,
): TCostListAlign => COLUMN_ALIGN[column];

// ----- Naam, Datum, Periode -----

type TListedSettlement = Pick<
  Settlement,
  "periodFrom" | "periodTo" | "snapshot" | "draftState"
>;

// The Naam cell: the settlement's name (the stored one, else its kind and
// period; settlementTitleOf). A departure names its tenants on a small line
// under it, as a cost's supplier is under its title (Lewis, 2026-10-16); any
// other settlement has no small line. No description (Lewis, 2026-10-16).
export const settlementNameTexts = (
  settlement: TListedSettlement,
): { title: string; subLines: string[] } => {
  const title = settlementTitleOf(settlement);
  if (settlementKindOf(settlement) !== "departure") {
    return { title, subLines: [] };
  }
  const tenantName = settlement.snapshot?.departure?.tenantName?.trim();
  return { title, subLines: tenantName ? [tenantName] : [] };
};

const NO_DAY = "—";

// The Datum cell: the day the settlement was confirmed, "16/10/2026"
// in Brussels time; a dash before that.
export const settlementConfirmedOnText = (
  settlement: Pick<Settlement, "confirmedAt">,
): string => {
  const day = toBrusselsIsoDay(settlement.confirmedAt);
  return day ? costDayText(day) : NO_DAY;
};

// The Periode cell, written as the cost list writes a cost's period.
export const settlementPeriodText = (
  settlement: Pick<Settlement, "periodFrom" | "periodTo">,
): string =>
  periodRangeText({ from: settlement.periodFrom, to: settlement.periodTo });

// The Bedrag cell: the total of the costs the settlement settled, excl.
// VAT, with "+ € x btw" under it, as the cost list writes a cost's amount
// (Lewis, 2026-10-21). From the figures it froze on confirm; a dash when it
// has none (an older snapshot, a draft).
export const settlementAmountTexts = (params: {
  settlement: Pick<Settlement, "snapshot">;
  format: (amount: number) => string;
}): { amount: string; subLines: string[] } => {
  const { settlement, format } = params;
  const figures = settlement.snapshot?.invoiceFigures;
  const ids = settlement.snapshot?.invoiceIds ?? [];
  if (!figures || ids.length === 0) return { amount: NO_DAY, subLines: [] };
  let exclCents = 0;
  let vatCents = 0;
  for (const id of new Set(ids)) {
    const cost = figures[id];
    if (!cost) continue;
    exclCents += toCents(cost.amountExclVat);
    vatCents += toCents(
      vatOfAmount({
        amountExclVat: cost.amountExclVat,
        vatPct: vatPercent(cost.vat),
      }),
    );
  }
  const vat = fromCents(vatCents);
  let vatLine: string | undefined;
  if (vat > 0) {
    vatLine = getLocalizedText("costs.detail.plus_vat", {
      amount: format(vat),
    });
  } else if (vat < 0) {
    vatLine = getLocalizedText("costs.detail.minus_vat", {
      amount: format(-vat),
    });
  }
  return {
    amount: format(fromCents(exclCents)),
    subLines: vatLine ? [vatLine] : [],
  };
};

// The texts every settlement row has, on every page.
export const settlementRowTexts = (
  settlement: TListedSettlement & Pick<Settlement, "confirmedAt" | "scope">,
): {
  name: string;
  nameSubLines: string[];
  confirmedOn: string;
  period: string;
  // For the filters (settlementListFilters.utils).
  typeKey: TSettlementTypeKey;
  periodFrom: string;
  periodTo: string;
} => {
  const { title, subLines } = settlementNameTexts(settlement);
  return {
    name: title,
    nameSubLines: subLines,
    confirmedOn: settlementConfirmedOnText(settlement),
    period: settlementPeriodText(settlement),
    typeKey: settlementTypeKeyOf(settlement),
    periodFrom: settlement.periodFrom,
    periodTo: settlement.periodTo,
  };
};

// What kind of settlement it is, as the Type column and its filter name
// it: the building's, a unit's own, or a final one on departure.
export type TSettlementTypeKey = "building" | "unit" | "departure";

export const settlementTypeKeyOf = (
  settlement: Pick<Settlement, "scope" | "snapshot" | "draftState">,
): TSettlementTypeKey => {
  if (settlementKindOf(settlement) === "departure") return "departure";
  return settlement.scope === "group" ? "building" : "unit";
};

// ----- Type -----

// The Type cell inside a building, in the cost list's own lozenges:
// "Gebouw" (blue) for the building's settlement, "Privaat" (grey) for a
// unit's, the unit's full address in its tooltip (no line under it), on the
// building page and the unit page alike. A departure holds the building's
// costs and the unit's own: it reads "Eindafrekening" (grey), the term the
// column's "?" explains (Lewis, 2026-10-21: an empty cell read as missing).
export interface ISettlementRowLevel {
  level: TCostLevel | "departure";
  label: string;
  appearance: TCostTypeAppearance;
  // The unit's full address ("Voorbeeldstraat 9 2, 9000 Gent"), for the
  // Privaat label's tooltip; undefined when the unit's address is unknown.
  unitAddress?: string;
}

export const settlementRowLevel = (params: {
  settlement: Pick<Settlement, "scope" | "unitId" | "snapshot" | "draftState">;
  // The building of the page; undefined for a unit on its own.
  buildingId?: string;
  // A unit's full address, as the property's header writes it.
  unitAddressOf: (unitId?: string) => string | undefined;
}): ISettlementRowLevel | undefined => {
  const { settlement, buildingId, unitAddressOf } = params;
  if (!buildingId) {
    return undefined;
  }
  if (settlementKindOf(settlement) === "departure") {
    return {
      level: "departure",
      label: getLocalizedText("settlement.title.departure_short"),
      appearance: "default",
    };
  }
  if (settlement.scope === "group") {
    return {
      level: "building",
      label: getLocalizedText("costs.label.building"),
      appearance: costTypeAppearance("building"),
    };
  }
  return {
    level: "unit",
    label: getLocalizedText("costs.label.private"),
    appearance: costTypeAppearance("unit"),
    unitAddress: unitAddressOf(settlement.unitId),
  };
};

// ----- Status -----

export interface ISettlementLozenge {
  label: string;
  appearance: "success" | "warning" | "info" | "default";
  // Under the lozenge: the day it was all done. An open status has no line
  // under it (Lewis, 2026-10-17).
  sub?: string;
  // Where the tenants are, for the status filter (settlementListFilters).
  progress?: TSettlementProgress;
}

export type TSettlementProgress = "open" | "done" | "nothing" | "draft";

// The settlement itself: "Concept" (grey) while it is a draft, "Bevestigd"
// (green) once confirmed. The Status cell shows a draft's (see
// settlementPayStatusOf).
export const settlementStateOf = (
  settlement: Pick<Settlement, "status">,
): ISettlementLozenge =>
  settlement.status === "confirmed"
    ? {
        label: getLocalizedText("costs.settlement.state.confirmed"),
        appearance: "success",
      }
    : {
        label: getLocalizedText("costs.settlement.state.draft"),
        appearance: "default",
        progress: "draft",
      };

// ----- Status: the payments -----

// The Status cell.
export type TSettlementPayStatus = ISettlementLozenge;

const MONEY_FROM = 0.005;
export const PAY_STATUS_SEPARATOR = " · ";

// The Betaalstatus cell: how far the settlement's tenants are, in both
// directions, as its drawer shows them. A tenant is done once they paid
// (isPaid, the drawer's own rule), once their refund is marked done
// (isRefunded: refundedAt, stored by "mark as refunded" in the drawer), or
// when they had nothing to pay or get back. Of all the settlement's tenants:
// - "0 van 4 afgehandeld" (amber) while none is done, "1 van 4
//   afgehandeld" (blue) while some are (no line under it: Lewis,
//   2026-10-17);
// - "Afgehandeld" (green), with the last day, once all are;
// - "Niets te betalen" (grey) when no tenant pays or gets anything back;
// - "Concept" (grey) for a draft, which only an older wizard stored.
export const settlementPayStatusOf = <
  Action extends SettlementAction & { refundedAt?: string | null },
>(params: {
  settlement: Pick<Settlement, "status">;
  // With their live payment links (getLiveSettlementActions).
  actions: Action[];
  isPaid: (action: Action) => boolean;
  paidAt: (action: Action) => string | undefined;
  isRefunded: (action: Action) => boolean;
  refundedAt: (action: Action) => string | undefined;
}): TSettlementPayStatus => {
  const { settlement, actions, isPaid, paidAt, isRefunded, refundedAt } =
    params;
  if (settlement.status !== "confirmed") return settlementStateOf(settlement);
  const movesMoney = (action: Action) => action.amount > MONEY_FROM;
  if (!actions.some(movesMoney)) {
    return {
      label: getLocalizedText("costs.settlement.status.nothing_to_pay"),
      appearance: "default",
      progress: "nothing",
    };
  }
  const toPay = actions.filter(
    action => movesMoney(action) && action.type === "invite" && !isPaid(action),
  ).length;
  const toRefund = actions.filter(
    action =>
      movesMoney(action) && action.type === "refund" && !isRefunded(action),
  ).length;
  const open = toPay + toRefund;
  if (open === 0) {
    const lastDoneOn = actions
      .filter(movesMoney)
      .map(action =>
        action.type === "refund" ? refundedAt(action) : paidAt(action),
      )
      .map(day => toBrusselsIsoDay(day))
      .filter((day): day is string => !!day)
      .sort()
      .at(-1);
    return {
      label: getLocalizedText("costs.settlement.status.completed"),
      appearance: "success",
      sub: lastDoneOn ? costDayText(lastDoneOn) : undefined,
      progress: "done",
    };
  }
  const done = actions.length - open;
  return {
    label: getLocalizedText("costs.settlement.status.done_of_total", {
      done: String(done),
      total: String(actions.length),
    }),
    appearance: done === 0 ? "warning" : "info",
    progress: "open",
  };
};
